import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { authApi } from '@/api'
import type { User, LoginRequest, RegisterRequest, SendCodeRequest } from '@/types'
import { ElMessage } from 'element-plus'
import { i18n } from '@/utils/i18n'

// Token storage keys
const TOKEN_KEY = 'flashhub_token'
const TOKEN_EXPIRY_KEY = 'flashhub_token_expiry'
const REMEMBER_ME_KEY = 'flashhub_remember_me'
const LANGUAGE_KEY = 'flashhub_language'

// Token expiry times (in milliseconds)
const TOKEN_EXPIRY_DEFAULT = 24 * 60 * 60 * 1000 // 24 hours
const TOKEN_EXPIRY_REMEMBER = 7 * 24 * 60 * 60 * 1000 // 7 days

export const useUserStore = defineStore('user', () => {
  // State
  const token = ref<string>(localStorage.getItem(TOKEN_KEY) || '')
  const user = ref<User | null>(null)
  const language = ref<string>(localStorage.getItem(LANGUAGE_KEY) || navigator.language || 'en')
  const rememberMe = ref<boolean>(localStorage.getItem(REMEMBER_ME_KEY) === 'true')
  
  // Getters
  const isLoggedIn = computed(() => {
    if (!token.value || !user.value) return false
    // Check token expiry
    const expiry = localStorage.getItem(TOKEN_EXPIRY_KEY)
    if (expiry && Date.now() > parseInt(expiry)) {
      logout()
      return false
    }
    return true
  })
  
  // Actions
  async function sendCode(data: SendCodeRequest) {
    await authApi.sendCode(data)
    return true
  }
  
  async function register(data: RegisterRequest) {
    try {
      const response = await authApi.register(data)
      const { user: userData, token: tokenData } = response.data.data
      user.value = userData
      token.value = tokenData
      saveToken(tokenData, true) // Auto-login after register with 7-day expiry
      syncLanguage()
      return true
    } catch (error) {
      return false
    }
  }
  
  async function login(data: LoginRequest, remember: boolean = false) {
    const response = await authApi.login(data)
    const { user: userData, token: tokenData } = response.data.data
    user.value = userData
    token.value = tokenData
    saveToken(tokenData, remember)
    rememberMe.value = remember
    localStorage.setItem(REMEMBER_ME_KEY, String(remember))
    syncLanguage()
    return true
  }
  
  function syncLanguage() {
    const lang = localStorage.getItem(LANGUAGE_KEY)
    if (lang && (lang === 'en' || lang === 'zh')) {
      i18n.setLocale(lang)
    }
  }
  
  // Save token with expiry
  function saveToken(tokenValue: string, remember: boolean) {
    localStorage.setItem(TOKEN_KEY, tokenValue)
    const expiryTime = Date.now() + (remember ? TOKEN_EXPIRY_REMEMBER : TOKEN_EXPIRY_DEFAULT)
    localStorage.setItem(TOKEN_EXPIRY_KEY, String(expiryTime))
  }
  
  async function fetchProfile() {
    if (!token.value) return false
    
    try {
      const response = await authApi.getProfile()
      user.value = response.data.data
      return true
    } catch (error) {
      logout()
      return false
    }
  }
  
  function logout() {
    token.value = ''
    user.value = null
    localStorage.removeItem(TOKEN_KEY)
    localStorage.removeItem(TOKEN_EXPIRY_KEY)
  }
  
  function setLanguage(lang: string) {
    language.value = lang
    localStorage.setItem(LANGUAGE_KEY, lang)
    i18n.setLocale(lang as 'en' | 'zh')
  }
  
  async function githubLogin() {
    try {
      const response = await authApi.getGitHubAuthUrl()
      const { auth_url } = response.data.data
      window.location.href = auth_url
    } catch (error) {
      ElMessage.error('Failed to get GitHub auth URL')
    }
  }
  
  return {
    // State
    token,
    user,
    language,
    rememberMe,
    // Getters
    isLoggedIn,
    // Actions
    sendCode,
    register,
    login,
    logout,
    fetchProfile,
    setLanguage,
    githubLogin,
  }
})